import { n as prepareMetadata, r as LogLevel, t as enabledLogLevels } from "./util-ugZPQ7Ug.js";
//#region src/transports/console.ts
const consoleTransport = (level, context, message, metadata, timestamp) => {
	const hasMetadata = metadata && Object.keys(metadata).length > 0;
	const isWeb = typeof window !== "undefined" && typeof window.document !== "undefined";
	const ansiColorize = withAnsiColor({
		["debug"]: ansiColors.magenta,
		["info"]: ansiColors.blue,
		["log"]: ansiColors.green,
		["warn"]: ansiColors.yellow,
		["error"]: ansiColors.red
	}[level]);
	const cssColor = {
		["debug"]: cssColors.magenta,
		["info"]: cssColors.blue,
		["log"]: cssColors.green,
		["warn"]: cssColors.yellow,
		["error"]: cssColors.red
	}[level] || cssColors.default;
	if (isWeb) logToWeb(cssColor, context, message, hasMetadata ? prepareMetadata(metadata) : void 0, timestamp);
	else logToNode(ansiColorize, context, message, hasMetadata ? prepareMetadata(metadata) : void 0, timestamp);
	if (message instanceof Error) console.error(message);
};
function formatTime(ts) {
	const d = new Date(ts);
	return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:${String(d.getSeconds()).padStart(2, "0")}`;
}
/**
* Minimal color helpers (no external deps).
* Same ANSI codes as kleur.
*/
const ansiColors = {
	default: [0, 0],
	blue: [36, 39],
	green: [32, 39],
	magenta: [35, 39],
	red: [31, 39],
	yellow: [33, 39]
};
const cssColors = {
	default: "#e5e7eb",
	blue: "#2563eb",
	green: "#16a34a",
	magenta: "#db2777",
	red: "#dc2626",
	yellow: "#ca8a04"
};
function withAnsiColor([x, y]) {
	const rgx = new RegExp(`\\x1b\\[${y}m`, "g");
	const open = `\x1b[${x}m`;
	const close = `\x1b[${y}m`;
	return function(txt) {
		if (txt === null || txt === void 0) return txt;
		return open + (~("" + txt).indexOf(close) ? txt.replace(rgx, close + open) : txt) + close;
	};
}
function logToWeb(cssColor, context, message, metadata, timestamp) {
	const parts = [];
	const styles = [];
	parts.push(`%c${formatTime(timestamp)}`);
	styles.push(`color: ${cssColor}`);
	if (context) {
		parts.push(` %c(${context})`);
		styles.push(`color: ${cssColor}`);
	}
	if (message) {
		parts.push(` %c${String(message)}`);
		styles.push("color: inherit");
	}
	if (metadata) {
		parts.push(` %c${JSON.stringify(metadata, null, 2)}`);
		styles.push("color: inherit");
	}
	console.log(parts.join(""), ...styles);
}
function logToNode(colorize, context, message, metadata, timestamp) {
	let msg = `${colorize(formatTime(timestamp))}`;
	if (context) msg += ` ${colorize(`(${context})`)}`;
	if (message) msg += ` ${String(message)}`;
	if (metadata) msg += ` ${JSON.stringify(metadata, null, 2)}`;
	console.log(msg);
}
//#endregion
//#region src/transports/ring.ts
function createRingTransport(capacity) {
	const buffer = [];
	return {
		transport: (level, context, message, metadata, timestamp) => {
			const prepared = metadata && Object.keys(metadata).length > 0 ? prepareMetadata(metadata) : void 0;
			buffer.push({
				at: timestamp,
				level,
				context,
				message: message instanceof Error ? message.message : String(message),
				...prepared ? { metadata: prepared } : {}
			});
			if (buffer.length > capacity) buffer.shift();
		},
		lines: () => [...buffer],
		clear: () => {
			buffer.length = 0;
		}
	};
}
//#endregion
//#region src/root.ts
/**
* Process-global sink for scoped loggers created via {@link getLogger}.
*
* Packages log through `getLogger('scope')` without knowing their destination;
* the host process decides where those logs go by calling
* {@link configureRootTransports} once at startup (e.g. the desktop app forwards
* them into electron-log's console/file/Axiom pipeline). Empty by default, so a
* non-configuring host (tests, a non-Electron embedder) stays silent.
*/
let rootTransports = [];
function configureRootTransports(transports) {
	rootTransports = transports;
}
function getLogger(context) {
	const emit = (level, message, metadata = {}) => {
		const timestamp = Date.now();
		for (const transport of rootTransports) transport(level, context, message, metadata, timestamp);
	};
	return {
		debug: (message, metadata) => emit("debug", message, metadata),
		info: (message, metadata) => emit("info", message, metadata),
		log: (message, metadata) => emit("log", message, metadata),
		warn: (message, metadata) => emit("warn", message, metadata),
		error: (message, metadata) => emit("error", message, metadata)
	};
}
//#endregion
//#region src/index.ts
var Logger = class Logger {
	static Level = LogLevel;
	level;
	context;
	contextFilter;
	debugContextRegexes = [];
	transports = [];
	static create({ level, context, contextFilter, transports = [], enableConsoleTransport = true } = {}) {
		const logger = new Logger({
			level,
			context,
			contextFilter
		});
		const list = [...transports || [], ...enableConsoleTransport ? [consoleTransport] : []].filter(Boolean);
		for (const t of list) logger.addTransport(t);
		return logger;
	}
	constructor({ level, context, contextFilter } = {}) {
		this.context = context;
		this.level = level || "info";
		this.contextFilter = contextFilter || "";
		if (this.contextFilter) this.level = "debug";
		this.debugContextRegexes = (this.contextFilter || "").split(",").filter(Boolean).map((filter) => {
			const sanitized = filter.replace(/[^\w:*-]/g, "").replace(/\*/g, ".*");
			try {
				return new RegExp(sanitized);
			} catch {
				return /* @__PURE__ */ new RegExp("");
			}
		});
	}
	debug(message, metadata = {}) {
		this.transport({
			level: "debug",
			message,
			metadata
		});
	}
	info(message, metadata = {}) {
		this.transport({
			level: "info",
			message,
			metadata
		});
	}
	log(message, metadata = {}) {
		this.transport({
			level: "log",
			message,
			metadata
		});
	}
	warn(message, metadata = {}) {
		this.transport({
			level: "warn",
			message,
			metadata
		});
	}
	error(error, metadata = {}) {
		this.transport({
			level: "error",
			message: error,
			metadata
		});
	}
	metric(event, metadata) {
		for (const t of this.transports) t("info", "metric", event, metadata, Date.now());
	}
	addTransport(transport) {
		this.transports.push(transport);
		return () => {
			const i = this.transports.indexOf(transport);
			if (i >= 0) this.transports.splice(i, 1);
		};
	}
	transport({ level, message, metadata = {} }) {
		const context = typeof metadata.context === "string" ? metadata.context : this.context;
		if (level === "debug" && !!this.contextFilter && !!context && !this.debugContextRegexes.find((reg) => reg.test(context))) return;
		const timestamp = Date.now();
		if (!enabledLogLevels[this.level].includes(level)) return;
		for (const t of this.transports) t(level, context, message, metadata, timestamp);
	}
};
//#endregion
export { LogLevel, Logger, configureRootTransports, consoleTransport, createRingTransport, enabledLogLevels, getLogger, prepareMetadata };

//# sourceMappingURL=index.js.map